import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ButtonProps } from '@chakra-ui/react';
import { DownloadIcon } from '@udacity/chakra-uds-icons';

/** Root element that includes the scaled certificate plus inline confirmation copy (must match print clone target). */
export const LP_CERTIFICATE_PRINT_ROOT_ID = 'lp-certificate-print-root';

const PRINT_CLONE_ID = 'cert-to-print';

type LpPrintRootDownloadButtonProps = ButtonProps & {
  onDownloadClick?: () => void;
};

/**
 * Clones `#lp-certificate-print-root` for print/PDF so inline confirmation copy is included
 * (LP equivalent of `DownloadCustomCertificateButton` from `@udacity/udacity-certificate`).
 */
export const LpPrintRootDownloadButton = ({ onDownloadClick, ...buttonProps }: LpPrintRootDownloadButtonProps) => {
  const { t } = useTranslation();
  const isPrintingRef = useRef(false);

  function handleDownloadCertificate() {
    if (isPrintingRef.current) return;

    onDownloadClick?.();

    const root = document.getElementById(LP_CERTIFICATE_PRINT_ROOT_ID);
    if (!root) return;

    const printStyles = createPrintStyles();
    const certToPrint = root.cloneNode(true) as HTMLElement;
    certToPrint.setAttribute('id', PRINT_CLONE_ID);
    certToPrint.style.display = 'none';
    document.body.appendChild(certToPrint);

    const cleanup = () => {
      isPrintingRef.current = false;
      printStyles.remove();
      certToPrint.remove();
    };
    window.addEventListener('afterprint', cleanup, { once: true });

    isPrintingRef.current = true;
    window.print();
  }

  return (
    <Button
      leftIcon={<DownloadIcon h={6} w={6} />}
      w='max-content'
      onClick={handleDownloadCertificate}
      {...buttonProps}
    >
      {t('certificate.downloadCertificate')}
    </Button>
  );
};

function createPrintStyles() {
  const printStyles = document.createElement('style');
  printStyles.innerHTML = `
    @media print {
      body *:not(#${PRINT_CLONE_ID}):not(#${PRINT_CLONE_ID} *) {
        display: none;
      }
      #${PRINT_CLONE_ID} {
        display: block !important;
      }

      @page {
        size: landscape;
        margin: 0 !important;
        padding: 0 !important;
      }
    }
  `;
  document.head.appendChild(printStyles);
  return printStyles;
}
